import {
  ErrorModal,
  MesfModal,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Button, Grid, TextField } from "@mui/material";
import { get, isNil } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../../controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "../../../../../../controls/snackbars/notification-snackbar";
import { BucketAutocomplete } from "../../../../../../shared/autocompletes/BucketAutocomplete";
import type { Bucket } from "../../../../../../shared/model/Bucket";
import { DateTimePickerDE } from "../../../../../../shared/data-entries/common/DateTimePickerDE";
import { modalStyles } from "@/shared/styles/modalStyles";
import { useDataEntryContext } from "../../../context/reducer.provider";
import { useEAFDataEntryActions } from "../../../hooks/useEAFDataEntryActions";
import { Charge } from "../../../models/eaf.models";
import { validateTimestamp } from "@/utils/validations";

type Props = {
  showModal: "NEW" | "EDIT" | "";
  onHide: () => void;
  charge: Charge | undefined;
};

export const ChargeModal = ({ showModal, onHide, charge }: Props) => {
  const { state } = useUTLSettingsContext();
  const maxAdditionWeight = parseInt(
    get(state.settings, "EAF_DE_ADDITIONS_MAX_WEIGHT", 5000),
  );
  const maxChargeWeight = parseInt(
    get(state.settings, "EAF_DE_CHARGES_MAX_WEIGHT", 200000),
  );

  const { saveCharge } = useEAFDataEntryActions();
  const {
    state: { deProcess },
  } = useDataEntryContext();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [notificationMessage, setNotificationMessage] = useState<string | null>(
    null,
  );
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  const [warningType, setWarningType] = useState<
    "addition" | "charge" | "multiple" | null
  >(null);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<Charge>({});

  const handleBucketSelected = (bucket: Bucket | null) => {
    if (bucket && bucket.IsClosed && bucket.BucketChargedWeight) {
      const weight = bucket.BucketChargedWeight;
      setValue("Weight", weight);
    } else {
      setValue("Weight", null);
    }
  };

  const onSubmit: SubmitHandler<Charge> = async (data: Charge) => {
    const warnings: string[] = [];
    if (data.Lime !== null && data.Lime > maxAdditionWeight) {
      warnings.push(`Lime value is above (${maxAdditionWeight}).`);
    }
    if (data.Weight !== null && data.Weight > maxChargeWeight) {
      warnings.push(`Weight value is above (${maxChargeWeight}).`);
    }

    if (warnings.length > 0) {
      setPendingData(data);
      setWarningType("multiple");
      setWarningMessages(warnings);
      setShowWarningModal(true);
      return;
    }

    setIsSubmitLoading(true);
    await saveCharge(data);
    setIsSubmitLoading(false);
    onHide();
  };

  useEffect(() => {
    if (showModal !== "") {
      if (showModal === "NEW") {
        reset({ Timestamp: deProcess?.process.Start });
      } else if (charge) {
        const formattedCharge = !isNil(charge?.Weight)
          ? parseInt(charge?.Weight.toFixed(0))
          : null;
        const formattedLime = !isNil(charge?.Lime)
          ? parseInt(charge?.Lime.toFixed(0))
          : null;
        reset({ ...charge, Weight: formattedCharge, Lime: formattedLime });
      }
    }
  }, [showModal, deProcess?.process.Start, charge, reset]);

  const handleProceedWarning = async () => {
    setShowWarningModal(false);
    if (pendingData) {
      setIsSubmitLoading(true);
      await saveCharge(pendingData);
      setIsSubmitLoading(false);
      onHide();
      setPendingData(null);
      setWarningType(null);
    }
  };

  const handleCancelWarning = () => {
    setShowWarningModal(false);
    setPendingData(null);
    setWarningType(null);
  };

  return (
    <>
      <Grid container>
        <Grid item>
          <MesfModal
            title={`${showModal} MANUAL CHARGE`}
            open={showModal !== ""}
            handleClose={() => onHide()}
            id="charge-modal"
            maxWidth="sm"
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <MesfModal.Content dividers sx={modalStyles.modalContent}>
                <Grid container spacing={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="Timestamp"
                        control={control}
                        defaultValue={charge?.Timestamp}
                        rules={{
                          required: "Timestamp is required",
                          validate: (value) =>
                            validateTimestamp(value, deProcess?.process.Start),
                        }}
                        render={({ field }) => (
                          <DateTimePickerDE
                            {...field}
                            format={"HH:mm"}
                            disableOpenPicker
                            label="Time"
                            fieldError={errors.Timestamp}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="BucketId"
                        control={control}
                        rules={{ required: "Bucket is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <BucketAutocomplete
                            label="Bucket"
                            value={field.value}
                            onChange={field.onChange}
                            onBucketSelected={handleBucketSelected}
                            fieldError={error}
                            isManualCharge={true}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="Weight"
                        control={control}
                        rules={{ required: "Weight is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            type="number"
                            variant="outlined"
                            fullWidth
                            label="Scrap Weight [lbs]"
                            error={!!error}
                            helperText={error?.message}
                            value={field.value ?? ""}
                            slotProps={{
                              inputLabel: {
                                shrink:
                                  field.value !== undefined &&
                                  field.value !== null,
                              },
                            }}
                            inputProps={{
                              min: 0,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="Lime"
                        control={control}
                        rules={{ required: "Lime is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            type="number"
                            variant="outlined"
                            fullWidth
                            label="Lime Weight [lbs]"
                            error={!!error}
                            helperText={error?.message}
                            inputProps={{
                              min: 0,
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </MesfModal.Content>
              <MesfModal.Actions sx={modalStyles.modalActions}>
                <FooterModalButtons
                  isSubmitLoading={isSubmitLoading}
                  onHide={onHide}
                />
              </MesfModal.Actions>
            </form>
          </MesfModal>
          <NotificationSnackBar
            message={notificationMessage}
            onHide={() => setNotificationMessage(null)}
            severity="success"
          />
        </Grid>
      </Grid>
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error Editing Manual Probe"
      />
      <MesfModal
        open={showWarningModal}
        handleClose={handleCancelWarning}
        title="Warning"
        id="warningModal"
      >
        <MesfModal.Content>
          {warningType === "multiple" ? (
            <div>
              <ul>
                {warningMessages.map((message, index) => (
                  <li key={index}>{message}</li>
                ))}
              </ul>
              <p>Are you sure you want to proceed?</p>
            </div>
          ) : warningType === "addition" ? (
            <p>
              Lime value is above ({maxAdditionWeight}). Do you want to proceed?
            </p>
          ) : warningType === "charge" ? (
            <p>
              Weight value is above ({maxChargeWeight}). Do you want to proceed?
            </p>
          ) : null}
        </MesfModal.Content>
        <MesfModal.Actions>
          <Button
            fullWidth
            variant="contained"
            color="inherit"
            onClick={handleCancelWarning}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleProceedWarning}
          >
            Proceed
          </Button>
        </MesfModal.Actions>
      </MesfModal>
    </>
  );
};
